import { CHAPTERS } from '../data/site';
import ChapterShell from '../components/ChapterShell';
import { MaskedLines, FadeUp, Eyebrow } from '../components/Reveal';

const C = CHAPTERS[2];
const AMBER = '#B8860B';

const STEPS = [
  { t: 'Hypothesis Formation', d: 'A core hypothesis gave the research a clear direction instead of broad, random inquiry.' },
  { t: 'Discussion Guide Development', d: 'A structured flow for interviews, open and qualitative, yet aimed at meaningful areas of inquiry.' },
  { t: 'Workflow Mapping', d: 'Detailed daily workflows of GT and MT stores: operational flow, roles, decision structures, ownership, movement of work.' },
  { t: 'Family Power Dynamics', d: 'Understanding how power and responsibility shift within a family store, where pressure builds and what breaks.' },
  { t: 'Case Study Analysis', d: 'Multiple retail cases documented and compared through tailored case study sheets.' },
  { t: 'Primary Observation Recording', d: 'Observation sheets translated conversations into usable, clear research notes.' },
  { t: 'Structured Note Processing', d: 'Notes colour coded into observations, idea sparks, pain points and insights.' },
  { t: 'Clustering', d: 'Related notes grouped into coherent thematic blocks from scattered signals.' },
  { t: 'Insight Formulation', d: 'Each insight shaped through What → Why → So What. An insight without implication is only an observation.' },
  { t: 'Insight Theme Grouping', d: 'Individual insights grouped under broader themes, easier to act upon.' },
  { t: 'JTBD Framework', d: 'Moving from understanding the ecosystem to understanding the user’s real progress.' },
  { t: 'Design Direction', d: 'Research outcomes formulated into the design brief, challenges and approach.' },
];

const GALLERY = [
  { src: '/assets/book/img-p109-22259.jpg', c: 'Counter conversations, where the real interviews happened' },
  { src: '/assets/book/new-photo1.jpg', c: 'Jars, eggs and judgment, the counter as inventory' },
  { src: '/assets/book/img-p108-22251.jpg', c: 'The market street, carts, cycles and morning trade' },
  { src: '/assets/book/img-p107-22149.jpg', c: 'The sasta bazaar, value written large on the shutters' },
  { src: '/assets/book/img-p113-22789.jpg', c: 'Owners at the counter, where loyalty lives' },
  { src: '/assets/book/img-p112-22687.jpg', c: 'Hanging SKUs, the original planogram' },
  { src: '/assets/book/img-p114-22889.jpg', c: 'Spice, grain and everything in between' },
  { src: '/assets/book/img-p106-22047.jpg', c: 'The neighbourhood provision store' },
  { src: '/assets/book/field-market1.jpg', c: 'The bazaar lane, stalls, shade and flow' },
  { src: '/assets/book/field-market2.jpg', c: 'The weekly market, where the whole neighbourhood shops' },
  { src: '/assets/book/img-p119-23147.jpg', c: 'Open-market mornings' },
  { src: '/assets/book/img-p118-23139.jpg', c: 'Retail that never really switches off' },
];

const Discovery = () => (
  <ChapterShell chapter={C} heroImg="/assets/book/img-p109-22259.jpg" heroCaption="Time on ground, conversations at the counter.">

    {/* 12 STEP APPROACH */}
    <section className="px-5 md:px-10 lg:px-24 py-24 md:py-36" data-testid="discovery-approach">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-32">
            <Eyebrow color={AMBER}>03 · Research Approach</Eyebrow>
            <MaskedLines
              lines={['From assumption', 'to grounded clarity,', 'in twelve steps.']}
              className="mt-10 text-4xl md:text-6xl font-light tracking-tighter leading-[1.02]"
            />
            <FadeUp delay={0.15}>
              <p className="mt-8 text-base md:text-lg text-ink/70 leading-relaxed max-w-md">
                A step by step inquiry built to first understand how the system works, where it
                breaks, why those breakdowns exist, and what they mean in a design context.
                Qualitative, but organised, traceable, and strategically actionable.
              </p>
            </FadeUp>
          </div>
        </div>
        <div className="lg:col-span-7">
          {STEPS.map((s, i) => (
            <FadeUp key={i} delay={0.02} y={24}>
              <div className="group grid grid-cols-12 gap-4 border-b border-ink/10 py-7 transition-colors duration-300" data-testid={`step-${i + 1}`}>
                <span className="col-span-2 md:col-span-1 text-sm font-light tracking-widest text-support group-hover:text-brand-amber transition-colors duration-300">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div className="col-span-10 md:col-span-11">
                  <p className="text-xl md:text-2xl font-medium tracking-tight">{s.t}</p>
                  <p className="mt-2 text-sm md:text-base text-ink/60 leading-relaxed max-w-xl">{s.d}</p>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>

    {/* DISCUSSION GUIDE + ARTEFACTS */}
    <section className="bg-brand-amber/10 px-5 md:px-10 lg:px-24 py-24 md:py-36" data-testid="discovery-artefacts">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
        <div>
          <Eyebrow color={AMBER}>Carrying a Conversation</Eyebrow>
          <FadeUp delay={0.08}>
            <p className="mt-8 text-lg md:text-2xl font-medium leading-relaxed text-ink/85">
              A discussion guide, not a questionnaire. A questionnaire captures answers, but not
              the reasoning behind them. The guide kept conversations flowing naturally while
              still covering what we needed to understand.
            </p>
          </FadeUp>
          <FadeUp delay={0.15}>
            <ul className="mt-8 space-y-2.5 text-sm md:text-base text-ink/70">
              {[
                'The journey of the shop, how it started and evolved',
                'Daily operations and how work actually flows',
                'Decision-making patterns and control within the store',
                'Changes in market behaviour and customer expectations',
                'Impact of new retail formats like quick commerce',
                'Operational constraints and everyday adjustments',
                'Fears, pressures, and the desire for change',
              ].map((t, i) => (
                <li key={i} className="flex gap-3">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-amber" />
                  {t}
                </li>
              ))}
            </ul>
          </FadeUp>
        </div>
        <div>
          <Eyebrow color={AMBER}>System Artefacts</Eyebrow>
          <FadeUp delay={0.08}>
            <p className="mt-8 text-base md:text-lg text-ink/70 leading-relaxed">
              Tools designed to structure and execute the research itself, converting scattered
              actions into an organised, traceable process. Three remained central throughout.
            </p>
          </FadeUp>
          <div className="mt-8 space-y-4">
            {[
              { t: 'Case Study Sheets', d: 'Tailored formats to document and compare retail cases systematically.' },
              { t: 'Primary Research Reports', d: 'Observation sheets translating conversations into usable notes.' },
              { t: 'Archetype Canvas', d: 'Mapping store archetypes and their operational logic.' },
            ].map((a, i) => (
              <FadeUp key={i} delay={i * 0.06}>
                <div className="border border-ink/15 bg-paper p-6 transition-colors duration-500 hover:bg-brand-amber group">
                  <p className="text-lg font-medium tracking-tight group-hover:text-ink">{a.t}</p>
                  <p className="mt-1.5 text-sm text-ink/60 leading-relaxed">{a.d}</p>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.2}>
            <img src="/assets/book/img-p104-21805.jpg" alt="Case study sheet artefact" data-testid="artefact-sheet-img" className="mt-8 w-full border border-ink/15 object-cover aspect-[16/10]" />
            <p className="mt-3 text-[11px] uppercase tracking-[0.25em] text-support">A filled case study sheet from the field</p>
          </FadeUp>
        </div>
      </div>
    </section>

    {/* FIELD GALLERY */}
    <section className="py-24 md:py-36 overflow-hidden" data-testid="discovery-gallery">
      <div className="px-5 md:px-10 lg:px-24">
        <Eyebrow color={AMBER}>Time on Ground, Field Gallery</Eyebrow>
        <MaskedLines
          lines={['The shop floor', 'was the studio.']}
          className="mt-10 text-4xl md:text-6xl font-light tracking-tighter leading-[1.02]"
        />
        <FadeUp delay={0.1}>
          <p className="mt-6 text-sm uppercase tracking-[0.25em] text-support">Drag or scroll sideways →</p>
        </FadeUp>
      </div>
      <FadeUp delay={0.15}>
        <div className="mt-14 flex gap-6 overflow-x-auto no-scrollbar snap-x snap-mandatory px-5 md:px-10 lg:px-24 pb-4" data-testid="gallery-track">
          {GALLERY.map((g, i) => (
            <figure key={i} className="group snap-center shrink-0 w-[80vw] sm:w-[55vw] md:w-[38vw] lg:w-[30vw]">
              <div className="overflow-hidden">
                <img
                  src={g.src}
                  alt={g.c}
                  loading="lazy"
                  className="aspect-[4/5] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <figcaption className="mt-3 flex gap-3 text-[11px] uppercase tracking-[0.2em] text-support">
                <span className="font-medium text-ink">{String(i + 1).padStart(2, '0')}</span>
                {g.c}
              </figcaption>
            </figure>
          ))}
        </div>
      </FadeUp>
    </section>
  </ChapterShell>
);

export default Discovery;
